import { useState } from 'react';

const FAQS = [
  {
    q: 'Who provides in-home senior care near me in Oakland, IL?',
    a: 'Oak Senior Care provides professional in-home senior care services in Oakland, IL and surrounding communities. Our trained caregivers assist with daily living, companionship, and specialized memory care, helping seniors remain safe, independent, and comfortable at home. We serve Coles, Douglas, Edgar, and Moultrie counties.',
    category: 'Getting Started',
  },
  {
    q: 'How much does in-home senior care cost in Illinois?',
    a: 'The cost of in-home senior care varies based on level of care, hours needed, and specific services required. Oak Senior Care offers flexible care plans with free consultations to provide accurate, personalized pricing. Some long-term care insurance policies may help cover in-home care costs.',
    category: 'Pricing',
  },
  {
    q: 'How quickly can in-home care start?',
    a: 'Care can often begin within a few days after your initial free consultation. Oak Senior Care works efficiently to assess your loved one\'s needs, create a personalized care plan, and carefully match the right caregiver. In urgent situations, we expedite the process whenever possible.',
    category: 'Getting Started',
  },
  {
    q: 'What type of senior care does my loved one need?',
    a: 'The right type of care depends on your loved one\'s health, daily challenges, and goals. We offer personal care, companion care, memory care, respite care, and post-hospital recovery support. During your free consultation, our coordinators help identify exactly what level and type of care is the best fit.',
    category: 'Services',
  },
  {
    q: 'Are Oak Senior Care caregivers background checked and trained?',
    a: 'Yes. Every Oak Senior Care caregiver undergoes thorough background screening, reference checks, and in-person interviews before hiring. All caregivers are trained in senior-specific care, CPR certified, and continuously monitored to ensure the highest quality and safety standards.',
    category: 'Trust & Safety',
  },
  {
    q: 'What makes Oak Senior Care different from other providers?',
    a: 'Oak Senior Care focuses on personalized, relationship-driven care. We are locally owned and operated in Oakland, IL, giving us deep community roots and fast response times. Each care plan is custom-built for the individual, and caregivers are carefully matched to ensure comfort, trust, and consistency.',
    category: 'About Us',
  },
  {
    q: 'Do you offer part-time and 24-hour senior care options?',
    a: 'Yes. Our care schedules are fully flexible — from a few hours per week to full-time daily care or 24/7 live-in support. We work around your family\'s existing routines and your loved one\'s specific needs to find the right arrangement.',
    category: 'Services',
  },
  {
    q: 'Is senior care covered by Medicare or insurance?',
    a: 'Medicare typically does not cover non-medical in-home care. However, some long-term care insurance policies, Medicaid waiver programs, and Veterans\' benefits may help cover costs. During your consultation, our team can help you understand your options and potential coverage.',
    category: 'Pricing',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-28 bg-white relative overflow-hidden" itemScope itemType="https://schema.org/FAQPage">
      {/* Background accent */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
        style={{ background: 'linear-gradient(0deg, rgba(247,249,248,1) 0%, transparent 100%)' }}
      />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">Voice Search Ready</p>
            <span className="inline-block w-10 h-px bg-gold opacity-70"></span>
          </div>
          <h2 className="font-heading text-navy font-bold mb-3 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            Answers to the most common questions families ask about in-home senior care in Oakland, IL.
          </p>
        </div>

        {/* Two-column accordion */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`rounded-2xl border overflow-hidden bg-offWhite transition-all duration-200 ${
                openIndex === i ? 'border-sage/60' : 'border-gray-100 hover:border-gray-200'
              }`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer gap-4"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <h4
                    className="font-heading text-navy font-semibold text-sm leading-snug"
                    itemProp="name"
                  >
                    <a href={`#faq-${i}`} className="hover:text-gold transition-colors">
                      {faq.q}
                    </a>
                  </h4>
                </div>
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all ${
                  openIndex === i ? 'bg-gold text-navy' : 'bg-white text-gray-400 border border-gray-200'
                }`}>
                  <i className={`${openIndex === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`}></i>
                </div>
              </button>

              <div
                id={`faq-${i}`}
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="mx-6 mb-5 border-l-2 border-sage/60 pl-4">
                  <p className="text-gray-500 text-sm leading-relaxed" itemProp="text">
                    {faq.a}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-4">Have a question not listed here?</p>
          <a
            href="tel:2173462042"
            className="whitespace-nowrap inline-flex items-center gap-2.5 bg-offWhite border border-gray-200 text-navy font-semibold px-6 py-3 rounded-full hover:border-sage hover:bg-sage/8 transition-all text-sm"
          >
            <i className="ri-phone-fill text-gold"></i>
            Call us directly: (217) 346-2042
          </a>
        </div>
      </div>
    </section>
  );
}
